/**
 * ParsedFeedbackBody — schema-aware dispatcher for rendering an LLM run's
 * parsed output. Resolves the effective OutputSchemaType from `outputSchemaType`
 * (override) or `PROMPT_OUTPUT_SCHEMA[promptType]` and renders the matching
 * schema view. Falls back to a JSON tree (or pre-formatted raw text) when the
 * schema isn't registered.
 *
 * Single source of truth for the dispatch — used by admin's Submission Detail
 * (`pages/submissions/parsedFeedback/ParsedFeedback.tsx`) and ModelRun Detail
 * (`pages/prompts/ModelRunDetail.tsx`). Page-specific decorations (collapsible
 * wrapper, quality-check badges, score header) live at the call site.
 */
import { PROMPT_OUTPUT_SCHEMA } from '@padcanvas/shared';
import type { Feedback, OutputSchemaType, PromptType } from '@padcanvas/shared';
import { ObjectiveCorrectnessView } from './ObjectiveCorrectnessView';
import { FreeformRubricView } from './FreeformRubricView';
import { DescriptiveAnswerView } from './DescriptiveAnswerView';
import { StructuredAssessmentView } from './StructuredAssessmentView';
import { ExtractedStepsView, type ExtractedStepsLike } from './ExtractedStepsView';
import { VisualsView, type SentStudentImage } from './VisualsView';
import { ExtractedQuestionsView } from './ExtractedQuestionsView';
import { HintView } from './HintView';
import { JsonTreeView, tryParseJson } from './JsonTreeView';
import { ConceptLabView, asConceptLab } from './ConceptLabView';
import { ExtractionValidationView, asExtractionValidation } from './ExtractionValidationView';
import {
    AssessmentQualityVerdictsView, EquivalenceVerdictsView, asJudgeBatch, judgeBatchFromRaw,
} from './JudgeVerdictsView';

export interface ParsedFeedbackBodyProps {
    /** Prompt type — drives schema lookup via PROMPT_OUTPUT_SCHEMA. */
    promptType?: string;
    /** Optional override. When the run carries an explicit schema that differs
     *  from its prompt's canonical schema, pass it here. Defaults to
     *  `PROMPT_OUTPUT_SCHEMA[promptType]`. */
    outputSchemaType?: OutputSchemaType | null;
    /** Parsed Feedback object (analysis / hint runs). */
    feedback?: Feedback | null;
    /** Extracted-data payload (extract_steps / extract_visuals runs). */
    extractedData?: unknown;
    /** Raw LLM response — used only as the fallback when no structured view
     *  is registered for the prompt type. */
    rawResponse?: string | null;
    /** Optional question context — passed through to score-bearing schema
     *  views that display the correct answer. */
    question?: { correctAnswer?: string };
    /** Learning moments detected on this submission, shown beside the parsed
     *  diagnosis. Only the solution-analysis surface loads them; everywhere else
     *  the sub-section shows the diagnosis alone. */
    learningMoments?: import('./AdminCrossCheckFields').SolutionLearningMoment[];
    /** Reject or restore one of those moments. Omitted ⇒ the list is read-only. */
    onRejectMoment?: import('./AdminCrossCheckFields').RejectMomentHandler;
    /** The student images this run sent the model, in order (absolute URLs).
     *  The visuals view draws `extract_visuals` boxes only on these. */
    sentStudentImages?: SentStudentImage[];
    /** The page images an extraction actually sent to the model, in page order
     *  (label `page-N`). Passed to the extracted-questions view so its diagram
     *  boxes can be overlaid on the right page. */
    pageImages?: { url: string; label: string }[];
}

/** Resolve `outputSchemaType` override → `PROMPT_OUTPUT_SCHEMA[promptType]`
 *  → `null`. Mirrors the resolution used inside both consumers prior to
 *  consolidation. */
export function resolveOutputSchemaType(
    promptType?: string,
    override?: OutputSchemaType | null,
): OutputSchemaType | null {
    if (override) return override;
    if (!promptType) return null;
    return PROMPT_OUTPUT_SCHEMA[promptType as PromptType] ?? null;
}

/** Build the `Feedback` shape `HintView` reads (hint payload on
 *  `llmAssessment`) from a hint run's raw JSON output. Returns null if the raw
 *  text isn't a hint-shaped object. Used because hint runs don't persist an
 *  analysis `assessmentDetails`/`feedback`. */
function hintFeedbackFromRaw(raw: string | null | undefined): Feedback | null {
    if (!raw) return null;
    const parsed = tryParseJson(raw);
    if (!parsed.ok || typeof parsed.value !== 'object' || parsed.value === null) return null;
    const obj = parsed.value as Record<string, unknown>;
    // Only treat as a hint payload if it carries the hint fields HintView renders.
    if (!('hint' in obj) && !('your_progress' in obj) && !('student_progress' in obj)) return null;
    return { llmAssessment: obj } as unknown as Feedback;
}

export function ParsedFeedbackBody({
    promptType,
    outputSchemaType,
    feedback,
    extractedData,
    rawResponse,
    question,
    learningMoments,
    onRejectMoment,
    sentStudentImages,
    pageImages,
}: ParsedFeedbackBodyProps) {
    const schema = resolveOutputSchemaType(promptType, outputSchemaType);

    switch (schema) {
        case 'ExtractedStepsV1':
            return extractedData
                ? <ExtractedStepsView data={extractedData as ExtractedStepsLike} />
                : <p className="empty">No extracted data on this run.</p>;
        case 'VisualsV1':
            // extract_visuals output rides on `extractedData` too — historically
            // typed as ExtractedStepsData but the runtime payload is a
            // Record<string, unknown> matching extractVisualsSchema.
            return extractedData
                ? <VisualsView data={extractedData as Record<string, unknown>} sentImages={sentStudentImages} />
                : <p className="empty">No extracted data on this run.</p>;
        case 'ExtractedQuestionsV1': {
            // A ModelRun row carries the LLM's raw output but no `extractedData`
            // — that field is written by the job that consumes the run, so a
            // per-run view had nothing and said "No extracted data" for a run
            // that plainly produced questions. Fall back to the raw JSON.
            const rawParsed = rawResponse ? tryParseJson(rawResponse) : null;
            const questions = extractedData ?? (rawParsed?.ok ? rawParsed.value : null);
            return questions && typeof questions === 'object'
                ? <ExtractedQuestionsView data={questions as Record<string, unknown>} pageImages={pageImages} />
                : <p className="empty">No extracted data on this run.</p>;
        }
        case 'ObjectiveCorrectnessV1':
            return feedback
                ? <ObjectiveCorrectnessView feedback={feedback} question={question} learningMoments={learningMoments} onRejectMoment={onRejectMoment} />
                : <p className="empty">No parsed assessment on this run.</p>;
        case 'FreeformRubricV1':
            return feedback
                ? <FreeformRubricView feedback={feedback} question={question} learningMoments={learningMoments} onRejectMoment={onRejectMoment} />
                : <p className="empty">No parsed assessment on this run.</p>;
        case 'StructuredAssessmentV1':
            return feedback
                ? <StructuredAssessmentView feedback={feedback} question={question} learningMoments={learningMoments} onRejectMoment={onRejectMoment} />
                : <p className="empty">No parsed assessment on this run.</p>;
        case 'DescriptiveAnswerV1':
            return feedback
                ? <DescriptiveAnswerView feedback={feedback} question={question} learningMoments={learningMoments} onRejectMoment={onRejectMoment} />
                : <p className="empty">No parsed assessment on this run.</p>;
        case 'HintV1': {
            // Hint runs don't write an analysis `assessmentDetails`, so `feedback`
            // is null here — the parsed hint lives only in the raw output.
            // Synthesize the shape HintView reads ({ llmAssessment: <hint payload> })
            // from the raw JSON so hints render instead of "No parsed hint".
            const hintFeedback = feedback ?? hintFeedbackFromRaw(rawResponse);
            return hintFeedback
                ? <HintView feedback={hintFeedback} />
                : <p className="empty">No parsed hint on this run.</p>;
        }
        case 'ExtractionValidationV1': {
            // The verdict is the run's raw JSON output — a judge step persists no
            // `extractedData` (it produces no questions) and no analysis
            // `feedback`. Still prefer `extractedData` when a caller has it, so a
            // future writer of that field needs no change here.
            const fromExtracted = asExtractionValidation(extractedData);
            const parsed = fromExtracted ? null : tryParseJson(rawResponse ?? '');
            const verdict = fromExtracted ?? (parsed?.ok ? asExtractionValidation(parsed.value) : null);
            return verdict
                ? <ExtractionValidationView data={verdict} />
                : <p className="empty">No parsed validation verdict on this run.</p>;
        }
        case 'EvalJudgeAssessmentQualityV1':
        case 'EvalJudgeEquivalenceV1': {
            // A judge run persists no analysis feedback and no extractedData —
            // its verdicts live only in the raw output, same as the extraction
            // validation judge.
            const batch = asJudgeBatch(extractedData) ?? judgeBatchFromRaw(rawResponse);
            if (!batch) return <p className="empty">No parsed verdicts on this run.</p>;
            return schema === 'EvalJudgeEquivalenceV1'
                ? <EquivalenceVerdictsView results={batch} />
                : <AssessmentQualityVerdictsView results={batch} />;
        }
        default: {
            // No registered structured view. Fall back to a best-effort render
            // of the raw response — a JSON tree if it parses, raw text
            // otherwise. Catch-all for prompt types without a formal output
            // schema (concept_lab, learning_moments, textchat, voice).
            if (!rawResponse) {
                return (
                    <p className="empty">
                        No parser registered for output schema {schema ? `"${schema}"` : '(none)'}, and the run produced no raw response.
                    </p>
                );
            }
            const parsed = tryParseJson(rawResponse);
            // concept_lab has no formalized OutputSchemaType, but its parsed
            // fields render nicely as labeled LaTeX sections — like solution
            // analysis — instead of a raw JSON dump.
            if (parsed.ok && promptType === 'concept_lab') {
                const concept = asConceptLab(parsed.value);
                if (concept) return <ConceptLabView data={concept} />;
            }
            return (
                <div className="parsed-feedback-fallback">
                    <p className="parsed-feedback-fallback-note">
                        No structured view for {schema ? <code>{schema}</code> : promptType ? <code>{promptType}</code> : 'this prompt type'} yet — showing the parsed raw response.
                    </p>
                    {parsed.ok
                        ? <JsonTreeView value={parsed.value} />
                        : <pre className="parsed-feedback-raw-text">{rawResponse}</pre>}
                </div>
            );
        }
    }
}
